import React, { useState, useEffect } from 'react';
import eventosService from '../services/eventosService';
import {
  clienteService,
  proyectoService,
  presupuestoCostoService
} from '../services/onebaseService';
import contentService from '../services/contentService';
import './PostModal.css';

const EventoModal = ({ evento, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'draft',
    tipo_evento: 'otro',
    cliente: '',
    proyecto: '',
    presupuesto_costo: '',
    desarrolla: '',
    fecha_inicio: '',
    fecha_termino: '',
    hora_inicio: '',
    hora_fin: '',
    ubicacion: '',
    capacidad_maxima: '',
    notas: '',
    etiqueta: '',
    etiqueta_color: '#3B82F6'
  });

  const [clientes, setClientes] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [proyectosFiltrados, setProyectosFiltrados] = useState([]);
  const [presupuestosCosto, setPresupuestosCosto] = useState([]);
  const [presupuestosCostoFiltrados, setPresupuestosCostoFiltrados] = useState([]);
  const [desarrollaOptions, setDesarrollaOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentSection, setCurrentSection] = useState('basic');

  useEffect(() => {
    loadOptions();
    if (evento) {
      setFormData({
        title: evento.title || '',
        description: evento.description || '',
        status: evento.status || 'draft',
        tipo_evento: evento.tipo_evento || 'otro',
        cliente: evento.cliente || '',
        proyecto: evento.proyecto || '',
        presupuesto_costo: evento.presupuesto_costo || '',
        desarrolla: evento.desarrolla || '',
        fecha_inicio: evento.fecha_inicio || '',
        fecha_termino: evento.fecha_termino || '',
        hora_inicio: evento.hora_inicio || '',
        hora_fin: evento.hora_fin || '',
        ubicacion: evento.ubicacion || '',
        capacidad_maxima: evento.capacidad_maxima || '',
        notas: evento.notas || '',
        etiqueta: evento.etiqueta || '',
        etiqueta_color: evento.etiqueta_color || '#3B82F6'
      });
    } else if (evento?.fecha_inicio) {
      setFormData(prev => ({
        ...prev,
        fecha_inicio: evento.fecha_inicio
      }));
    }
  }, [evento]);

  // Filtrar proyectos cuando cambia el cliente
  useEffect(() => {
    // El campo p.cliente puede venir como entero (FK id) o como objeto {id, nombre}
    const clienteId = parseInt(formData.cliente);
    const matchCliente = (p) => {
      if (!p.cliente) return false;
      if (typeof p.cliente === 'object') return p.cliente.id === clienteId;
      return parseInt(p.cliente) === clienteId;
    };

    if (formData.cliente) {
      const proyectosPorCliente = proyectos.filter(matchCliente);
      setProyectosFiltrados(proyectosPorCliente);

      // Si el proyecto ya seleccionado no pertenece al nuevo cliente, limpiarlo
      if (formData.proyecto) {
        const proyectoActual = proyectos.find(p => p.id === parseInt(formData.proyecto));
        if (proyectoActual && !matchCliente(proyectoActual)) {
          setFormData(prev => ({ ...prev, proyecto: '' }));
        }
      }
    } else {
      // Sin cliente seleccionado, mostrar todos los proyectos
      setProyectosFiltrados(proyectos);
    }
  }, [formData.cliente, proyectos, formData.proyecto]);

  // Filtrar presupuestos cuando cambia el proyecto
  useEffect(() => {
    if (formData.proyecto) {
      const presupuestosPorProyecto = presupuestosCosto.filter(p => p.proyecto === parseInt(formData.proyecto));
      setPresupuestosCostoFiltrados(presupuestosPorProyecto);

      if (formData.presupuesto_costo) {
        const presupuestoActual = presupuestosCosto.find(p => p.id === parseInt(formData.presupuesto_costo));
        if (presupuestoActual && presupuestoActual.proyecto !== parseInt(formData.proyecto)) {
          setFormData(prev => ({ ...prev, presupuesto_costo: '' }));
        }
      }
    } else {
      setPresupuestosCostoFiltrados(presupuestosCosto);
    }
  }, [formData.proyecto, presupuestosCosto, formData.presupuesto_costo]);

  const loadOptions = async () => {
    try {
      // Cargar opciones con manejo de errores individual
      const results = await Promise.allSettled([
        clienteService.getAll({ page_size: 1000 }),
        proyectoService.getAll({ page_size: 1000 }),
        presupuestoCostoService.getAll({ page_size: 1000 }),
        contentService.getDesarrolla()
      ]);

      // Procesar clientes
      if (results[0].status === 'fulfilled') {
        const clientesRes = results[0].value;
        setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : (clientesRes.data.results || []));
      } else {
        console.error('Error loading clientes:', results[0].reason);
        setClientes([]);
      }

      // Procesar proyectos
      if (results[1].status === 'fulfilled') {
        const proyectosRes = results[1].value;
        const proyectosData = Array.isArray(proyectosRes.data) ? proyectosRes.data : (proyectosRes.data.results || []);
        setProyectos(proyectosData);
        setProyectosFiltrados(proyectosData);
      } else {
        console.error('Error loading proyectos:', results[1].reason);
        setProyectos([]);
        setProyectosFiltrados([]);
      }

      // Procesar presupuestos
      if (results[2].status === 'fulfilled') {
        const presupuestosRes = results[2].value;
        const presupuestosData = Array.isArray(presupuestosRes.data) ? presupuestosRes.data : (presupuestosRes.data.results || []);
        setPresupuestosCosto(presupuestosData);
        setPresupuestosCostoFiltrados(presupuestosData);
      } else {
        console.error('Error loading presupuestos:', results[2].reason);
        setPresupuestosCosto([]);
        setPresupuestosCostoFiltrados([]);
      }

      // Procesar desarrolla
      if (results[3].status === 'fulfilled') {
        const desarrollaRes = results[3].value;
        setDesarrollaOptions(Array.isArray(desarrollaRes.data) ? desarrollaRes.data : (desarrollaRes.data.results || []));
      } else {
        console.error('Error loading desarrolla:', results[3].reason);
        setDesarrollaOptions([]);
      }
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'El título es requerido';
    if (!formData.fecha_inicio) newErrors.fecha_inicio = 'La fecha de inicio es requerida';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      if (errors.title || errors.fecha_inicio) {
        setCurrentSection('basic');
      } else {
        setCurrentSection('details');
      }
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        cliente: formData.cliente || null,
        proyecto: formData.proyecto || null,
        presupuesto_costo: formData.presupuesto_costo || null,
        desarrolla: formData.desarrolla || null,
        fecha_termino: formData.fecha_termino || null,
        hora_inicio: formData.hora_inicio || null,
        hora_fin: formData.hora_fin || null,
        capacidad_maxima: formData.capacidad_maxima || null
      };

      if (evento?.id) {
        await eventosService.updateEvento(evento.id, submitData);
      } else {
        await eventosService.createEvento(submitData);
      }

      onClose();
    } catch (error) {
      console.error('Error saving evento:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        alert('Error al guardar el evento');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvento = async () => {
    // Confirmar eliminación
    if (!window.confirm('¿Estás seguro de que deseas eliminar este evento? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      console.log('🗑️ Eliminando evento con ID:', evento.id);
      await eventosService.deleteEvento(evento.id);
      alert('✅ Evento eliminado exitosamente');

      // Cerrar modal y recargar
      onClose();

      // Recargar la página después de un pequeño delay
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('Error al eliminar evento:', error);
      alert(error.response?.data?.error || 'Error al eliminar el evento');
    }
  };

  const calculateProgress = () => {
    const requiredFields = ['title', 'fecha_inicio'];
    const filledFields = requiredFields.filter(field => {
      const value = formData[field];
      return value && value.toString().trim() !== '';
    });
    return Math.round((filledFields.length / requiredFields.length) * 100);
  };

  const progress = calculateProgress();

  const TIPO_EVENTO_OPTIONS = [
    { value: 'evento', label: 'Evento' },
    { value: 'proyecto', label: 'Proyecto' },
    { value: 'otro', label: 'Otro' }
  ];

  const STATUS_OPTIONS = [
    { value: 'draft', label: 'Borrador' },
    { value: 'planning', label: 'En Planificación' },
    { value: 'confirmed', label: 'Confirmado' },
    { value: 'in_progress', label: 'En Progreso' },
    { value: 'completed', label: 'Completado' },
    { value: 'cancelled', label: 'Cancelado' },
    { value: 'postponed', label: 'Pospuesto' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-l" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div className="modal-header-icon">🎯</div>
            <div className="modal-header-text">
              <h2 className="modal-header-title">{evento?.id ? 'Editar Evento' : 'Crear Nuevo Evento'}</h2>
            </div>
          </div>
          <button onClick={onClose} className="modal-close-btn">✕</button>
        </div>

        {/* Barra de progreso */}
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            <span className="progress-text">{progress}% completado</span>
          </div>
        </div>

        {/* Navegación por pestañas */}
        <div className="tabs-navigation">
          <button
            className={`tab ${currentSection === 'basic' ? 'active' : ''}`}
            onClick={() => setCurrentSection('basic')}
            type="button"
          >
            <span className="tab-icon">📋</span>
            <span className="tab-text">Información Básica</span>
            {Object.keys(errors).some(key => ['title', 'fecha_inicio'].includes(key)) && (
              <span className="tab-badge">!</span>
            )}
          </button>
          <button
            className={`tab ${currentSection === 'details' ? 'active' : ''}`}
            onClick={() => setCurrentSection('details')}
            type="button"
          >
            <span className="tab-icon">📝</span>
            <span className="tab-text">Detalles del Evento</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Sección: Información Básica */}
          {currentSection === 'basic' && (
            <div className="form-section animate-in">
              <div className="form-grid">
                {/* Título */}
                <div className="form-group full-width">
                  <label>
                    Título del Evento <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <span className="input-icon">🎯</span>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Ej: Lanzamiento de producto 2024"
                      className={errors.title ? 'error' : ''}
                    />
                  </div>
                  {errors.title && <span className="error-message">{errors.title}</span>}
                </div>

                {/* Descripción */}
                <div className="form-group full-width">
                  <label>Descripción</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe brevemente el evento..."
                  />
                </div>

                {/* Etiqueta */}
                <div className="form-group full-width">
                  <label>Etiqueta <span className="helper-text" style={{ fontWeight: 'normal' }}>— visible en la card del calendario</span></label>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <div className="input-wrapper" style={{ flex: 1 }}>
                      <span className="input-icon">🏷️</span>
                      <input
                        type="text"
                        name="etiqueta"
                        value={formData.etiqueta}
                        onChange={handleChange}
                        placeholder="Ej: Urgente, Aprobado, Revisión..."
                        maxLength={50}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                      <label style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>Color:</label>
                      <input
                        type="color"
                        name="etiqueta_color"
                        value={formData.etiqueta_color || '#3B82F6'}
                        onChange={handleChange}
                        style={{ width: '2.5rem', height: '2.5rem', padding: '2px', border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: 'pointer', background: 'none' }}
                        title="Color de la etiqueta"
                      />
                    </div>
                    {formData.etiqueta && (
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: '9999px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        backgroundColor: formData.etiqueta_color || '#3B82F6',
                        color: '#fff',
                        letterSpacing: '0.03em',
                        flexShrink: 0,
                        whiteSpace: 'nowrap'
                      }}>
                        {formData.etiqueta}
                      </span>
                    )}
                  </div>
                  <span className="helper-text">Opcional. Aparecerá como un badge en la card del evento.</span>
                </div>

                {/* Tipo de Evento y Estado */}
                <div className="form-group">
                  <label>Tipo de Evento</label>
                  <div className="input-wrapper">
                    <span className="input-icon"></span>
                    <select name="tipo_evento" value={formData.tipo_evento} onChange={handleChange}>
                      {TIPO_EVENTO_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Estado</label>
                  <div className="input-wrapper">
                    <span className="input-icon">📊</span>
                    <select name="status" value={formData.status} onChange={handleChange}>
                      {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Cliente */}
                <div className="form-group">
                  <label>Cliente</label>
                  <div className="input-wrapper">
                    <span className="input-icon">🏢</span>
                    <select name="cliente" value={formData.cliente} onChange={handleChange}>
                      <option value="">Seleccionar cliente...</option>
                      {clientes.map(c => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Proyecto */}
                <div className="form-group">
                  <label>Proyecto</label>
                  <div className="input-wrapper">
                    <span className="input-icon">📁</span>
                    <select
                      name="proyecto"
                      value={formData.proyecto}
                      onChange={handleChange}
                      disabled={!formData.cliente}
                    >
                      <option value="">
                        {formData.cliente ? 'Seleccionar proyecto...' : 'Primero selecciona un cliente'}
                      </option>
                      {proyectosFiltrados.map(p => (
                        <option key={p.id} value={p.id}>{p.nombre}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Desarrolla */}
                <div className="form-group">
                  <label>Desarrolla</label>
                  <div className="input-wrapper">
                    <span className="input-icon">🔧</span>
                    <select name="desarrolla" value={formData.desarrolla} onChange={handleChange}>
                      <option value="">Seleccionar...</option>
                      {desarrollaOptions.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Presupuesto Costo */}
                <div className="form-group full-width">
                  <label>Presupuesto de Costo</label>
                  <div className="input-wrapper">
                    <span className="input-icon">💰</span>
                    <select
                      name="presupuesto_costo"
                      value={formData.presupuesto_costo}
                      onChange={handleChange}
                      disabled={!formData.proyecto}
                    >
                      <option value="">
                        {formData.proyecto ? 'Seleccionar presupuesto...' : 'Primero selecciona un proyecto'}
                      </option>
                      {presupuestosCostoFiltrados.map(p => (
                        <option key={p.id} value={p.id}>{p.nombre || `Presupuesto #${p.id}`}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Fechas */}
                <div className="form-group">
                  <label>
                    Fecha de Inicio <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <span className="input-icon">📅</span>
                    <input
                      type="date"
                      name="fecha_inicio"
                      value={formData.fecha_inicio}
                      onChange={handleChange}
                      className={errors.fecha_inicio ? 'error' : ''}
                    />
                  </div>
                  {errors.fecha_inicio && <span className="error-message">{errors.fecha_inicio}</span>}
                </div>

                <div className="form-group">
                  <label>Fecha de Término</label>
                  <div className="input-wrapper">
                    <span className="input-icon">📅</span>
                    <input
                      type="date"
                      name="fecha_termino"
                      value={formData.fecha_termino}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Hora de Inicio</label>
                  <div className="input-wrapper">
                    <span className="input-icon">🕐</span>
                    <input
                      type="time"
                      name="hora_inicio"
                      value={formData.hora_inicio}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Hora de Fin</label>
                  <div className="input-wrapper">
                    <span className="input-icon">🕐</span>
                    <input
                      type="time"
                      name="hora_fin"
                      value={formData.hora_fin}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="section-footer">
                <button
                  type="button"
                  onClick={() => setCurrentSection('details')}
                  className="estado-btn"
                >
                  Siguiente: Detalles del Evento
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Sección: Detalles del Evento */}
          {currentSection === 'details' && (
            <div className="form-section animate-in">
              <div className="form-content-grid">
                {/* Ubicación */}
                <div className="form-group full-width">
                  <label>Ubicación</label>
                  <div className="input-wrapper">
                    <span className="input-icon">📍</span>
                    <input
                      type="text"
                      name="ubicacion"
                      value={formData.ubicacion}
                      onChange={handleChange}
                      placeholder="Dirección física o enlace virtual"
                    />
                  </div>
                  <span className="helper-text">Dirección del evento o enlace para eventos virtuales</span>
                </div>

                {/* Capacidad Máxima */}
                <div className="form-group">
                  <label>Capacidad Máxima</label>
                  <div className="input-wrapper">
                    <span className="input-icon">👥</span>
                    <input
                      type="number"
                      name="capacidad_maxima"
                      value={formData.capacidad_maxima}
                      onChange={handleChange}
                      min="0"
                      placeholder="Número de asistentes"
                    />
                  </div>
                </div>

                {/* Notas */}
                <div className="form-group full-width">
                  <label>Notas y Observaciones</label>
                  <textarea
                    name="notas"
                    value={formData.notas}
                    onChange={handleChange}
                    rows="6"
                    placeholder="Notas adicionales, requerimientos especiales, contactos importantes..."
                  />
                  <span className="helper-text">Información adicional relevante para el evento</span>
                </div>
              </div>

              <div className="section-footer with-back">
                <button
                  type="button"
                  onClick={() => setCurrentSection('basic')}
                  className="btn-back"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Volver
                </button>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {evento?.id && (
                    <button
                      type="button"
                      onClick={handleDeleteEvento}
                      className="btn-delete-evento"
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                      onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                    >
                      🗑️ Eliminar Evento
                    </button>
                  )}
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        {evento?.id ? 'Actualizando...' : 'Creando...'}
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {evento?.id ? 'Actualizar Evento' : 'Crear Evento'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EventoModal;
